import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";

const CompaniesContext = createContext();

export const useCompaniesContext = () => {
  const context = useContext(CompaniesContext);
  if (!context) {
    throw new Error('useCompaniesContext must be used within a CompaniesProvider');
  }
  return context;
};

export const CompaniesProvider = ({ children }) => {
  const { list: listCompanies, save, update, remove, toggleBlockStatus, cleanCompanyFiles } = useCompanies();
  const { list: listPlans } = usePlans();
  
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCompaniesAndPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companiesData, plansData] = await Promise.all([
        listCompanies(),
        listPlans(),
      ]);

      const validCompanies = Array.isArray(companiesData)
        ? companiesData.filter(c => c && c.id)
        : [];
        
      const validPlans = Array.isArray(plansData)
        ? plansData.filter(p => p && p.id)
        : [];

      setCompanies(validCompanies);
      setPlans(validPlans);
    } catch (err) {
      console.error("Error fetching companies and plans:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [listCompanies, listPlans]);

  // Load data on initial mount
  useEffect(() => {
    fetchCompaniesAndPlans();
  }, [fetchCompaniesAndPlans]);

  const value = {
    companies,
    plans,
    loading,
    error,
    fetchCompaniesAndPlans,
    saveCompany: save,
    updateCompany: update,
    removeCompany: remove,
    toggleBlockStatus,
    cleanCompanyFiles
  };

  return (
    <CompaniesContext.Provider value={value}>
      {children}
    </CompaniesContext.Provider>
  );
};

export default CompaniesContext;